<template>
  <v-card>
    <v-toolbar card dark color="primary">
      <v-toolbar-title>Maximum Seconds Per Location and Variety</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="closeMaximumSecondsDialog">
          <v-icon>close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-container v-if="loading" fluid fill-height class="white">
      <loading></loading>
    </v-container>
    <v-container v-else-if="loadError" fluid fill-height class="white">
      <v-layout row wrap align-content-start pa-2>
        <v-flex xs12>
          <h1 class="text-xs-center">Plant Alerts</h1>
        </v-flex> 
        <v-flex xs12>
          <v-alert 
            :value="true" 
            outline 
            type="error">
            <span>{{ loadError }}</span>
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
    <v-form v-else class="fill-height" v-model="formValid">
      <v-container fluid fill-height class="white">
        <v-layout row wrap align-content-start pa-2>
          <v-flex v-if="saveError" xs12>
            <v-alert 
              :value="true" 
              outline 
              type="error">
              <span>{{ saveError }}</span>
            </v-alert>
          </v-flex>
          <v-flex xs12>
            <v-btn color="primary" :disabled="!formValid" @click="saveChanges" :loading="saving">
              <span>Save Changes</span>
            </v-btn>
            <v-btn color="primary" :disabled="pinnedLocationVarieties.length == 0" @click="openBatchEditDialog">
              <span>Batch Edit ({{ pinnedLocationVarieties.length }})</span>
            </v-btn>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Locations"
              :items="availableLocationIds"
              multiple
              clearable
              v-model="filteredLocationIds">
            </v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Varieties"
              :items="availableProductVarietyIds"
              multiple
              clearable
              v-model="filteredProductVarietyIds">
            </v-autocomplete>
          </v-flex>
          <template v-if="filteredLocationVarieties.length > 0">
            <v-flex xs12>
              <v-data-table
                :headers="headers"
                :pagination.sync="pagination"
                :rows-per-page-items="rowsPerPageItems"
                :items="filteredLocationVarieties">
                <template v-slot:items="{ item }">
                  <td>
                    <v-btn icon @click="togglePinKey(item.key)">
                      <v-icon v-if="pinnedKeys.indexOf(item.key) >= 0">check_box</v-icon>
                      <v-icon v-else>check_box_outline_blank</v-icon>
                    </v-btn>
                  </td>
                  <td>
                    <span class="subheading">{{ item.locationName }}</span>
                  </td>
                  <td>
                    <span class="subheading">{{ item.productVarietyName }}</span>
                  </td>
                  <td>
                    <v-text-field
                      label="Maximum Seconds % Threshold"
                      suffix="%"
                      clearable
                      placeholder="Use Plant Default"
                      :rules="[validationRules.number({ min: 0, max: 100, decimalPlaces: 0 })]"
                      v-model="item.maximumSeconds">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      label="Alert Snooze Period"
                      suffix="hr"
                      clearable
                      placeholder="Use Plant Default"
                      :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                      v-model="item.maximumSecondsSnoozePeriod">
                    </v-text-field>
                  </td>
                </template>
              </v-data-table>
            </v-flex>
          </template>
          <template v-else>
            <v-flex xs12>
              <v-alert :value="true" type="info">
                <span>There are no location / variety pairs of the current filter selection.</span>
              </v-alert>
            </v-flex>
          </template>
        </v-layout>
      </v-container>
    </v-form>
    <v-dialog v-if="batchDialogVisible" :value="true" persistent @keydown.esc.stop="closeBatchEditDialog">
      <v-card>
        <v-toolbar card dark color="primary">
          <v-toolbar-title>Batch Edit</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeBatchEditDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form v-model="batchEditFormValid">
          <v-container fluid class="white">
            <v-layout row wrap pa-2>
              <v-flex xs12>
                <v-alert :value="true" type="info">
                  <span>This will apply the same values across the selected location / variety pairs.</span>
                </v-alert>
              </v-flex>
              <template v-if="pinnedLocationVarieties.length > 0">
                <v-flex xs6 class="pa-2">
                  <v-text-field
                    label="Maximum seconds % threshold"
                    suffix="%"
                    clearable
                    placeholder="Use Plant Default"
                    :rules="[validationRules.number({ min: 0, max: 100, decimalPlaces: 0 })]"
                    v-model="batchEditMaximumSeconds">
                  </v-text-field>
                </v-flex>
                <v-flex xs6 class="pa-2">
                  <v-text-field
                    label="Alert snooze period"
                    suffix="hr"
                    clearable
                    placeholder="Use Plant Default"
                    :rules="[validationRules.number({ min: 0, max: 9999, decimalPlaces: 3 })]"
                    v-model="batchEditMaximumSecondsSnoozePeriod">
                  </v-text-field>
                </v-flex>
                <template v-for="locationVariety in pinnedLocationVarieties">
                  <v-chip close :key="locationVariety.key" @input="unpinKey(locationVariety.key)">
                    <span class="subheading mr-2">{{ locationVariety.locationName }}</span>
                    <span class="subheading">{{ locationVariety.productVarietyName }}</span>
                  </v-chip>
                </template>
              </template>
              <template v-else>
                <v-flex xs12>
                  <v-alert :value="true" type="warning">
                    <span>There are no location / variety pairs selected for batch edit.</span>
                  </v-alert>
                </v-flex>
              </template>
            </v-layout>
          </v-container>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="confirmBatchEditDialog" :disabled="pinnedLocationVarieties.length == 0 || !batchEditFormValid" color="primary">Apply</v-btn>
          <v-btn @click="closeBatchEditDialog" color="error">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import BaseAPI from "@/api/baseApi";
import plantConfigurationApi from '@/api/plantConfigurationApi';
import Loading from '@/components/generic/Loading.vue';
import validationRules from '@/helpers/validationrules';
import utilService from '@/services/utilService';
const formatNumber = utilService.formatNumber;

export default {
  components: {
    Loading
  },
  props: {
    plantID: {
      type: Number,
      required: true
    }
  },
  data(){
    return {
      validationRules: validationRules.rules,
      loading: false,
      loadError: null,
      formValid: false,
      pinnedKeys: [],
      locationVarieties: [],
      filteredLocationIds: [],
      filteredProductVarietyIds: [],
      headers: [
        {
          text: 'Batch Edit',
          sortable: false
        },
        {
          text: 'Location',
          value: 'locationName'
        },
        {
          text: 'Variety',
          value: 'productVarietyName'
        },
        {
          sortable: false
        },
        {
          sortable: false
        }
      ],
      rowsPerPageItems: [20],
      pagination: {
        rowsPerPage: 20
      },
      batchDialogVisible: false,
      batchEditFormValid: false,
      batchEditMaximumSeconds: null,
      batchEditMaximumSecondsSnoozePeriod: null,
      saving: false,
      saveError: null
    };
  },
  computed: {
    pinnedLocationVarieties(){
      return this.locationVarieties.filter(x => this.pinnedKeys.indexOf(x.key) >= 0);
    },
    availableLocationIds(){
      return Object.values(
        this.locationVarieties.reduce((a, v) => {
          if(a[v.locationId] == null){
            a[v.locationId] = {
              text: v.locationName,
              value: v.locationId
            };
          }

          return a;
        }, {})
      );
    },
    availableProductVarietyIds(){
      return Object.values(
        this.locationVarieties.reduce((a, v) => {
          if(a[v.productVarietyId] == null){
            a[v.productVarietyId] = {
              text: v.productVarietyName,
              value: v.productVarietyId
            };
          }

          return a;
        }, {})
      );
    },
    filteredLocationVarieties(){
      return this.locationVarieties.filter(
        x => (
            (
              this.filteredLocationIds.indexOf(x.locationId) >= 0 
              || this.filteredLocationIds.length == 0
            ) && (
              this.filteredProductVarietyIds.indexOf(x.productVarietyId) >= 0 
              || this.filteredProductVarietyIds.length == 0
            )
          )
      );
    }
  },
  methods: {
    async init(){
      await this.loadData();
    },
    async loadData(){
      this.loading = true;

      this.loadError = null;
      this.resetPlantAlertSecondsSettings();
      try {
        let plantAlertSecondsSettings = await plantConfigurationApi.getPlantAlertSecondsSettings(this.plantID);

        this.loadPlantAlertSecondsSettings(plantAlertSecondsSettings);
      } catch (err){
        this.loadError = await BaseAPI.parseHttpRequestError(err);
        
        console.log(err);
      }

      this.loading = false;
    },
    resetPlantAlertSecondsSettings(){
      this.pinnedKeys.splice(0, this.pinnedKeys.length);
      this.locationVarieties.splice(0, this.locationVarieties.length);
    },
    loadPlantAlertSecondsSettings(plantAlertSecondsSettings){
      let locationVarieties = plantAlertSecondsSettings.map(x => {
        return {
          key: `${x.LocationID}-${x.ProductVarietyID}`,
          locationId: x.LocationID,
          locationName: x.LocationName,
          productVarietyId: x.ProductVarietyID,
          productVarietyName: x.ProductVarietyName,
          maximumSeconds: formatNumber(x.MaximumSeconds == null ? null : x.MaximumSeconds * 100, 0),
          maximumSecondsSnoozePeriod: formatNumber(x.MaximumSecondsSnoozePeriod, 3)
        };
      });

      this.locationVarieties.splice(0, this.locationVarieties.length, ...locationVarieties);
    },
    togglePinKey(key){
      let index = this.pinnedKeys.indexOf(key);
      if(index < 0){
        this.pinnedKeys.push(key);
      } else {
        this.pinnedKeys.splice(index, 1);
      }
    },
    pinKey(key){
      if(this.pinnedKeys.indexOf(key) >= 0){
        return;
      }

      this.pinnedKeys.push(key);
    },
    unpinKey(key){
      let index = this.pinnedKeys.indexOf(key);
      if(index < 0){
        return;
      }

      this.pinnedKeys.splice(index, 1);
    },
    openBatchEditDialog(){
      this.batchEditMaximumSeconds = null;
      this.batchEditMaximumSecondsSnoozePeriod = null;

      this.batchDialogVisible = true;
    },
    closeBatchEditDialog(){
      this.batchDialogVisible = false;
    },
    confirmBatchEditDialog(){
      for(let i = 0; i < this.pinnedLocationVarieties.length; i++){
        this.pinnedLocationVarieties[i].maximumSeconds = this.batchEditMaximumSeconds;
        this.pinnedLocationVarieties[i].maximumSecondsSnoozePeriod = this.batchEditMaximumSecondsSnoozePeriod;
      }

      this.closeBatchEditDialog();
      this.pinnedKeys.splice(0, this.pinnedKeys.length);
    },
    async saveChanges(){
      this.saving = true;

      this.saveError = null;
      try {
        let plantAlertSecondsSettings = this.locationVarieties.map(x => {
          return {
            LocationID: x.locationId,
            LocationName: x.locationName,
            ProductVarietyID: x.productVarietyId,
            ProductVarietyName: x.productVarietyName,
            MaximumSeconds: x.maximumSeconds == null || x.maximumSeconds === "" ? null : x.maximumSeconds / 100,
            MaximumSecondsSnoozePeriod: x.maximumSecondsSnoozePeriod
          };
        });

        let result = await plantConfigurationApi.savePlantAlertSecondsSettings(this.plantID, plantAlertSecondsSettings);
        if(!result.Success){
          this.saveError = result.ErrorMessages.join("\n");

          console.log(result.ErrorMessages)
        }
      } catch (err){
        this.saveError = await BaseAPI.parseHttpRequestError(err);

        console.log(err);
      }

      this.saving = false;
    },
    closeMaximumSecondsDialog(){
      this.$emit("close");
    }
  },
  created(){
    this.init();
  }
}
</script>